import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const STAGE_COLORS = {
  founder_held: 'text-red-700',
  foundation_building: 'text-amber-700',
  growing_founder_dependent: 'text-[#C8A15A]',
  systems_driven: 'text-blue-700',
  scale_ready: 'text-emerald-700',
};

export default function ScorecardAdmin() {
  const [data, setData] = useState({ items: [], total: 0, stats: { by_stage: {} }, stages: [], categories: [] });
  const [stage, setStage] = useState('all');
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (stage !== 'all') params.stage = stage;
      if (q.trim()) params.q = q.trim();
      const r = await adminApi.get('/admin/scorecard', { params });
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load scorecard submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stage]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    adminApi.get(`/admin/scorecard/${selectedId}`).then((r) => setDetail(r.data)).catch(() => toast.error('Failed to load detail'));
  }, [selectedId]);

  const catName = (id) => data.categories.find((c) => c.id === id)?.name || id;

  return (
    <div className="px-6 lg:px-10 py-8">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} data-testid="scorecard-admin-title">
            Founder Systems Scorecard
          </h1>
          <p className="text-sm text-[#666] mt-1">28-question lead-generation assessment. Submissions auto-join The Founder's List.</p>
        </div>
        <a
          href={`${BACKEND_URL}/api/admin/scorecard/export.csv`}
          target="_blank"
          rel="noreferrer"
          data-testid="scorecard-export-csv"
          className="text-xs tracking-[0.18em] uppercase px-4 py-2 border border-[#111] text-[#111] hover:bg-[#111] hover:text-[#FAF8F5] rounded-full"
        >
          Export CSV
        </a>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        <StatCard label="Total" value={data.stats.total || 0} testid="stat-total" />
        {data.stages.map((s) => (
          <StatCard key={s.key} label={s.name} value={data.stats.by_stage[s.key] || 0} testid={`stat-${s.key}`} />
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <FilterChip active={stage === 'all'} onClick={() => setStage('all')} testid="filter-all">All</FilterChip>
        {data.stages.map((s) => (
          <FilterChip key={s.key} active={stage === s.key} onClick={() => setStage(s.key)} testid={`filter-${s.key}`}>
            {s.name}
          </FilterChip>
        ))}
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or email"
          data-testid="scorecard-search"
          className="ml-auto px-4 py-2 border border-[#eae4d8] rounded-full text-sm focus:border-[#C8A15A] focus:outline-none"
        />
      </div>

      {/* Table */}
      <div className="border border-[#eae4d8] rounded-xl overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF8F5] text-[#888] text-[11px] tracking-[0.14em] uppercase">
            <tr>
              <th className="text-left px-4 py-3 font-normal">Submitted</th>
              <th className="text-left px-4 py-3 font-normal">Name</th>
              <th className="text-left px-4 py-3 font-normal">Email</th>
              <th className="text-left px-4 py-3 font-normal">Score</th>
              <th className="text-left px-4 py-3 font-normal">Stage</th>
              <th className="text-left px-4 py-3 font-normal">Weakest</th>
              <th className="text-left px-4 py-3 font-normal">List</th>
              <th className="text-left px-4 py-3 font-normal">Email</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center py-10 text-[#888]">Loading…</td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-[#888]" data-testid="scorecard-empty">No submissions yet.</td></tr>
            )}
            {data.items.map((it) => (
              <tr key={it.id} className="border-t border-[#eae4d8] hover:bg-[#FAF8F5] cursor-pointer" onClick={() => setSelectedId(it.id)} data-testid={`scorecard-row-${it.id}`}>
                <td className="px-4 py-3 text-[#555]">{fmtDate(it.created_at)}</td>
                <td className="px-4 py-3 text-[#111]">{it.first_name}</td>
                <td className="px-4 py-3 text-[#555]">{it.email}</td>
                <td className="px-4 py-3 font-serif text-lg text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{it.overall_score}/100</td>
                <td className={`px-4 py-3 ${STAGE_COLORS[it.result_stage_key] || 'text-[#111]'}`}>{it.result_stage_name}</td>
                <td className="px-4 py-3 text-[#555]">{catName(it.weakest_category_id)}</td>
                <td className="px-4 py-3 text-xs">{it.subscriber_result || '—'}</td>
                <td className="px-4 py-3 text-xs">{it.email_sent ? <span className="text-emerald-700">Sent</span> : <span className="text-red-600">Failed</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Drawer */}
      {selectedId && (
        <Drawer onClose={() => setSelectedId(null)} detail={detail} />
      )}
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="bg-white border border-[#eae4d8] rounded-xl p-4" data-testid={testid}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#888]">{label}</div>
      <div className="font-serif text-3xl text-[#111] mt-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{value}</div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-xs tracking-[0.14em] uppercase px-4 py-1.5 rounded-full border transition-all ${
        active ? 'bg-[#111] text-[#FAF8F5] border-[#111]' : 'bg-white text-[#555] border-[#eae4d8] hover:border-[#111]'
      }`}
    >
      {children}
    </button>
  );
}

function Drawer({ onClose, detail }) {
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="scorecard-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-[#FAF8F5] h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#FAF8F5] border-b border-[#eae4d8] px-6 py-4 flex items-center justify-between">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#888]">Scorecard Submission</div>
          <button onClick={onClose} className="text-sm text-[#555] hover:text-[#111]" data-testid="scorecard-drawer-close">Close ×</button>
        </div>
        {!detail && <div className="px-6 py-16 text-center text-[#888]">Loading…</div>}
        {detail && (
          <div className="px-6 py-6 space-y-8">
            <div>
              <div className="font-serif text-2xl text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{detail.first_name}</div>
              <div className="text-sm text-[#555]">{detail.email}</div>
              <div className="text-xs text-[#888] mt-1">Submitted {fmtDate(detail.created_at)}</div>
            </div>

            <div className="bg-white border border-[#eae4d8] rounded-xl p-6">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#888]">Score</div>
                  <div className="font-serif text-5xl text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{detail.overall_score}<span className="text-[#C8A15A] text-2xl">/100</span></div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#888]">Stage</div>
                  <div className="font-serif text-xl text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{detail.result_stage_name}</div>
                </div>
              </div>
              {detail.result_stage && (
                <p className="mt-4 text-sm text-[#555] leading-relaxed">{detail.result_stage.summary}</p>
              )}
            </div>

            {/* Category breakdown */}
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#888] mb-3">Category Breakdown</div>
              <div className="bg-white border border-[#eae4d8] rounded-xl divide-y divide-[#eae4d8]">
                {detail.categories?.map((c) => (
                  <div key={c.id} className="px-5 py-4 flex items-center gap-4">
                    <div className="flex-1">
                      <div className="font-serif text-lg text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{c.name}</div>
                      <div className="mt-1 h-[3px] bg-[#f1ece3] rounded-full">
                        <div className="h-full bg-[#C8A15A] rounded-full" style={{ width: `${c.percentage}%` }} />
                      </div>
                    </div>
                    <div className="font-serif text-xl text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{c.percentage}%</div>
                    <div className="text-xs text-[#888] w-14 text-right">{c.raw}/16</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <HighlightCard label="Strongest" value={detail.spec_categories?.find((c) => c.id === detail.strongest_category_id)?.name} />
              <HighlightCard label="Opportunity" value={detail.spec_categories?.find((c) => c.id === detail.weakest_category_id)?.name} tone="gold" />
            </div>

            <div className="bg-white border border-[#eae4d8] rounded-xl p-5">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#888] mb-2">Next System To Build</div>
              <p className="text-sm text-[#111] leading-relaxed">{detail.next_system_to_build}</p>
            </div>
            <div className="bg-[#111] text-[#FAF8F5] rounded-xl p-5">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#C8A15A] mb-2">AI Opportunity</div>
              <p className="text-sm leading-relaxed">{detail.ai_opportunity}</p>
            </div>

            {/* Full answers */}
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#888] mb-3">All 28 Answers</div>
              <div className="space-y-6">
                {detail.spec_categories?.map((cat) => {
                  const answers = (detail.answers || []).filter((a) => a.category_id === cat.id);
                  return (
                    <div key={cat.id}>
                      <div className="font-serif text-lg text-[#111] mb-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                        {cat.name} {cat.reverse && <span className="ml-2 text-[10px] tracking-[0.18em] uppercase text-[#C8A15A]">Reverse scored</span>}
                      </div>
                      <ol className="space-y-2 text-sm">
                        {cat.questions.map((q, idx) => {
                          const ans = answers.find((a) => a.question_index === idx);
                          const labelList = cat.reverse ? ['Absolutely', 'Mostly', 'Somewhat', 'A little', 'Not at all'] : ['Not at all', 'A little', 'Somewhat', 'Mostly', 'Absolutely'];
                          return (
                            <li key={idx} className="bg-white border border-[#eae4d8] rounded-lg p-3">
                              <div className="text-[#111]">{q}</div>
                              <div className="text-xs text-[#888] mt-1">Answered: <span className="text-[#111]">{ans ? labelList[ans.value] : '—'}</span></div>
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Delivery */}
            <div className="bg-white border border-[#eae4d8] rounded-xl p-5 space-y-2 text-sm">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#888] mb-2">Delivery</div>
              <div>Results Email: {detail.email_sent ? <span className="text-emerald-700">Sent {fmtDate(detail.email_sent_at)}</span> : <span className="text-red-600">Failed — {detail.email_error}</span>}</div>
              <div className="text-xs text-[#888] break-all">Message ID: {detail.email_message_id || '—'}</div>
              <div>Subscriber: <span className="text-[#111]">{detail.subscriber_result || '—'}</span></div>
              <div>Source: <span className="text-[#111]">{detail.source || '—'}</span></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function HighlightCard({ label, value, tone }) {
  const bg = tone === 'gold' ? 'bg-[#C8A15A] text-[#111]' : 'bg-white text-[#111] border border-[#eae4d8]';
  return (
    <div className={`${bg} rounded-xl p-5`}>
      <div className="text-[10px] tracking-[0.22em] uppercase mb-1 opacity-70">{label}</div>
      <div className="font-serif text-xl" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{value || '—'}</div>
    </div>
  );
}
